import React, { useEffect, useRef, useMemo } from 'react';

export default function ParticleFusion({ top = '67.5%', left = '50.7%' }) {
  const canvasRef = useRef(null);

  // Pre-generate all random values once so the visual is deterministic
  const particleData = useMemo(() => {
    const particles = Array.from({ length: 120 }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 25 + Math.random() * 200;
      return {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance - (Math.random() * 80),
        size: Math.random() * 3.5 + 1.5,
        duration: 1.5 + Math.random() * 3,
        delay: Math.random() * 2,
        maxOpacity: Math.random() * 0.7 + 0.5,
      };
    });

    const synapses = Array.from({ length: 12 }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      return {
        angle,
        length: 40 + Math.random() * 100,
        delay: Math.random() * 5,
        duration: 0.5 + Math.random() * 1.5,
        repeatDelay: 1 + Math.random() * 4,
      };
    });

    const lightRays1 = Array.from({ length: 14 }).map(() => ({
      angle: (Math.random() * 360) * (Math.PI / 180),
      length: 100 + Math.random() * 180,
      endWidth: 15 + Math.random() * 40,
      opacity: 0.05 + Math.random() * 0.15,
    }));

    const lightRays2 = Array.from({ length: 12 }).map(() => ({
      angle: (Math.random() * 360) * (Math.PI / 180),
      length: 150 + Math.random() * 220,
      endWidth: 20 + Math.random() * 50,
      opacity: 0.03 + Math.random() * 0.1,
    }));

    return { particles, synapses, lightRays1, lightRays2 };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let animationFrameId;
    let startTime = performance.now();

    // Canvas sizing — generous to hold all particle drift
    const W = 600;
    const H = 600;
    const CX = W / 2;
    const CY = H / 2;
    canvas.width = W;
    canvas.height = H;

    const { particles, synapses, lightRays1, lightRays2 } = particleData;

    const render = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      ctx.clearRect(0, 0, W, H);

      // Set screen blending
      ctx.globalCompositeOperation = 'screen';

      // ─── 1. Deep Core Glow ───
      const coreScale = 0.8 + 0.4 * Math.sin(elapsed * (Math.PI * 2 / 4)) * 0.5 + 0.5;
      const coreOp = 0.4 + 0.4 * (Math.sin(elapsed * (Math.PI * 2 / 4)) * 0.5 + 0.5);
      ctx.save();
      ctx.globalAlpha = coreOp;
      const coreR = 90 * coreScale;
      const coreGrad = ctx.createRadialGradient(CX, CY, 0, CX, CY, coreR);
      coreGrad.addColorStop(0, 'rgba(255,170,68,0.3)');
      coreGrad.addColorStop(1, 'rgba(255,170,68,0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(CX, CY, coreR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // ─── 2. Intense Center Orb ───
      const orbScale = 0.9 + 0.4 * (Math.sin(elapsed * (Math.PI * 2 / 1.5)) * 0.5 + 0.5);
      const orbOp = 0.8 + 0.2 * (Math.sin(elapsed * (Math.PI * 2 / 1.5)) * 0.5 + 0.5);
      ctx.save();
      ctx.globalAlpha = orbOp;
      const orbR = 6 * orbScale;
      ctx.shadowBlur = 15;
      ctx.shadowColor = 'rgba(255,170,68,0.8)';
      ctx.fillStyle = '#ffeedd';
      ctx.beginPath();
      ctx.arc(CX, CY, orbR, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.restore();

      // ─── 3. Horizontal Flare ───
      const flareScaleX = 0.6 + 0.8 * (Math.sin(elapsed * (Math.PI * 2 / 2.5)) * 0.5 + 0.5);
      const flareOp = 0.5 + 0.4 * (Math.sin(elapsed * (Math.PI * 2 / 2.5)) * 0.5 + 0.5);
      ctx.save();
      ctx.globalAlpha = flareOp;
      ctx.fillStyle = '#ffcc66';
      const flareW = 140 * flareScaleX;
      ctx.fillRect(CX - flareW / 2, CY - 1, flareW, 2);
      ctx.restore();

      // ─── 4. Secondary Faint Horizontal Flare ───
      const flare2ScaleX = 0.8 + 0.4 * (Math.sin((elapsed - 0.5) * (Math.PI * 2 / 3.5)) * 0.5 + 0.5);
      const flare2Op = 0.3 + 0.3 * (Math.sin((elapsed - 0.5) * (Math.PI * 2 / 3.5)) * 0.5 + 0.5);
      ctx.save();
      ctx.globalAlpha = flare2Op * 0.6;
      ctx.filter = 'blur(4px)';
      ctx.fillStyle = '#ff6600';
      const flare2W = 250 * flare2ScaleX;
      ctx.fillRect(CX - flare2W / 2, CY - 4, flare2W, 8);
      ctx.filter = 'none';
      ctx.restore();

      // ─── 5. Subtle Vertical Flare ───
      const vFlareScaleY = 0.7 + 0.6 * (Math.sin((elapsed - 0.2) * (Math.PI * 2 / 2)) * 0.5 + 0.5);
      const vFlareOp = 0.3 + 0.4 * (Math.sin((elapsed - 0.2) * (Math.PI * 2 / 2)) * 0.5 + 0.5);
      ctx.save();
      ctx.globalAlpha = vFlareOp * 0.5;
      ctx.fillStyle = '#ffcc66';
      const vFlareH = 70 * vFlareScaleY;
      ctx.fillRect(CX - 1, CY - vFlareH / 2, 2, vFlareH);
      ctx.restore();

      // ─── 6. Drifting Particles (Embers) ───
      particles.forEach(p => {
        const cycleDuration = p.duration;
        const totalCycle = cycleDuration + p.delay;
        const t = ((elapsed % totalCycle) - p.delay) / cycleDuration;
        if (t < 0 || t > 1) return;

        // Ease in-out
        const eased = t < 0.5
          ? 2 * t * t
          : 1 - Math.pow(-2 * t + 2, 2) / 2;

        const px = CX + p.x * eased;
        const py = CY + p.y * eased;
        // Fade in, hold, fade out
        const opacity = t < 0.2 ? t / 0.2 : t > 0.8 ? (1 - t) / 0.2 : 1;
        const scale = t < 0.2 ? t / 0.2 * 1.5 : t > 0.8 ? (1 - t) / 0.2 * 1.5 : 1.5;
        const size = p.size * scale;

        ctx.save();
        ctx.globalAlpha = Math.min(1, opacity * p.maxOpacity);
        ctx.shadowBlur = 4;
        ctx.shadowColor = '#ffaa44';
        ctx.fillStyle = '#ffeedd';
        ctx.beginPath();
        ctx.arc(px, py, Math.max(0.5, size / 2), 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.restore();
      });

      // ─── 7. Volumetric Light Rays (Group 1 - Clockwise) ───
      const ray1Rotation = (elapsed / 60) * Math.PI * 2;
      ctx.save();
      ctx.translate(CX, CY);
      ctx.rotate(ray1Rotation);
      lightRays1.forEach(ray => {
        ctx.save();
        ctx.rotate(ray.angle);
        ctx.globalAlpha = ray.opacity;
        const grad = ctx.createLinearGradient(0, 0, ray.length, 0);
        grad.addColorStop(0, `rgba(255,200,100,${ray.opacity})`);
        grad.addColorStop(1, 'rgba(255,170,68,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(ray.length, -ray.endWidth / 2);
        ctx.lineTo(ray.length, ray.endWidth / 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      });
      ctx.restore();

      // ─── Light Rays (Group 2 - Counter-Clockwise) ───
      const ray2Rotation = -(elapsed / 90) * Math.PI * 2;
      ctx.save();
      ctx.translate(CX, CY);
      ctx.rotate(ray2Rotation);
      lightRays2.forEach(ray => {
        ctx.save();
        ctx.rotate(ray.angle);
        ctx.globalAlpha = ray.opacity;
        const grad = ctx.createLinearGradient(0, 0, ray.length, 0);
        grad.addColorStop(0, `rgba(255,170,68,${ray.opacity})`);
        grad.addColorStop(1, 'rgba(255,150,50,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(ray.length, -ray.endWidth / 2);
        ctx.lineTo(ray.length, ray.endWidth / 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      });
      ctx.restore();

      // ─── 9. Neural Synapse Flashes ───
      synapses.forEach(s => {
        const totalCycle = s.duration + s.repeatDelay;
        const cycleTime = (elapsed - s.delay) % (totalCycle);
        if (cycleTime < 0 || cycleTime > s.duration) return;

        const t = cycleTime / s.duration;
        const opacity = t < 0.3 ? t / 0.3 * 0.8 : (1 - t) / 0.7 * 0.8;
        const scaleX = t < 0.5 ? t / 0.5 : 0.4 + 0.6 * (1 - t);
        const slideX = t * 15;

        ctx.save();
        ctx.translate(CX + slideX, CY);
        ctx.rotate(s.angle);
        ctx.globalAlpha = Math.max(0, opacity);
        const grad = ctx.createLinearGradient(0, 0, s.length * scaleX, 0);
        grad.addColorStop(0, '#ffeedd');
        grad.addColorStop(1, 'rgba(255,238,221,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(s.length * scaleX, 0);
        ctx.stroke();
        ctx.restore();
      });

      // Reset compositing
      ctx.globalCompositeOperation = 'source-over';

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [particleData]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute pointer-events-none mix-blend-screen"
      style={{
        top,
        left,
        transform: 'translate(-50%, -50%)',
        width: '600px',
        height: '600px',
        zIndex: 10,
      }}
    />
  );
}
